// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Shared device/viewport heuristics for UI behaviors. */

const KEYBOARD_SHRINK_THRESHOLD = 120; // px; tuned to avoid small UI changes

let initialViewportHeight: number | null = null;

/** Reads the live motion preference; gesture-driven pinch, drag and fling are exempt. */
export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function measureViewportHeight(): number {
  const vvHeight =
    typeof window !== "undefined" && window.visualViewport ? window.visualViewport.height : null;
  return Math.max(0, vvHeight ?? window.innerHeight ?? 0);
}

/**
 * Capture the initial viewport height exactly once. Optionally accepts a value from callers
 * (e.g., AppShell/main) to lock a shared baseline across consumers.
 */
export function initializeViewportHeightOnce(value?: number): number {
  if (initialViewportHeight == null) {
    const measured = value != null ? value : measureViewportHeight();
    initialViewportHeight = Math.max(0, measured);
  }
  return initialViewportHeight;
}

/** Returns the cached initial viewport height, measuring once if not yet initialized. */
export function getInitialViewportHeight(): number {
  return initializeViewportHeightOnce();
}

/**
 * Check whether the environment is likely touch-capable.
 *
 * Heuristics used:
 * - navigator.maxTouchPoints > 1: modern signal for multi-touch hardware
 *   (using >1 helps avoid some false positives on devices reporting a single pointer).
 * - matchMedia('(any-pointer: coarse)'): broader indicator for touch-oriented pointers.
 *
 * @returns True if touch input is likely available.
 */
export function hasTouch(): boolean {
  return (
    (navigator.maxTouchPoints || 0) > 1 ||
    window.matchMedia?.("(any-pointer: coarse)")?.matches === true
  );
}

export function isLikelyMobile(): boolean {
  const ua = navigator.userAgent || "";
  const mobileUA = /(Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini)/i.test(ua);
  const touch = hasTouch();
  const narrow =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 768px)").matches === true;
  return mobileUA || (touch && narrow);
}

export function isVirtualKeyboardLikelyOpen(initialViewportHeight: number): boolean {
  const currentHeight = window.visualViewport?.height ?? window.innerHeight ?? 0;
  if (!currentHeight || !initialViewportHeight) return false;
  const shrink = initialViewportHeight - currentHeight;
  return shrink > KEYBOARD_SHRINK_THRESHOLD;
}

export function isElementOutOfViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height ?? window.innerHeight ?? 0;
  if (!viewportHeight) return false;
  return rect.bottom < 0 || rect.top > viewportHeight;
}
